import React, { useEffect, useMemo, useState } from 'react';
import type { SchemaEngine } from 'schema';
import type { Poll as PollType, TrueTheme } from 'schema/poll';
import { NewLoaderOverlay } from './components/NewLoaderOverlay';
import { useCryptoContext } from './contexts/cryptoContext';
import { RootContext } from './contexts/rootContext';
import { useVariableContext } from './contexts/variableContext';
import './crypto';
import { loadPollEntrypoint } from './loadPollEntrypoint';
import './style_modern.module.css';
import './style_old.module.css';
import type TrackingEngine from './trackingEngine';
import { injectVariablesIntoPoll } from './utils/injectVariablesIntoPoll';
import { isWebview } from './utils/location';

export interface GlobalContext {
  trackingEngine: TrackingEngine;
  schemaEngine: SchemaEngine;
  maxWidth?: PollType['maxWidth'];
  minHeight?: PollType['minHeight'];
  template: PollType['template'];
  theme: TrueTheme['data'];
  prefersColorScheme: 'dark' | 'light';
}

export const PollEntrypointLoader: React.FC<{
  poll: PollType;
  title: string | null;
  theme: TrueTheme['data'];
  prefersColorScheme: 'dark' | 'light';
}> = ({ poll, title, theme, prefersColorScheme }) => {
  const template = poll.template;
  const { isLoading: isCryptoLoading } = useCryptoContext();
  const { variables, isLoading: isVariableLoading } = useVariableContext();
  const [config, setConfig] = useState<PollComponentConfig | null>(null);
  const { component: Component, trackingEngine, schemaEngine } = config ?? { component: React.Fragment };

  useEffect(() => {
    void loadPollEntrypoint(poll).then(config => {
      setConfig(config);
    });
  }, []);

  const pollWithInjectedVariables = useMemo(
    () => (variables ? injectVariablesIntoPoll(poll, variables) : poll),
    [variables, poll],
  );

  if (isCryptoLoading || isVariableLoading || !schemaEngine || !trackingEngine) {
    return <NewLoaderOverlay hideContentId flat isWebview={isWebview()} />;
  }

  return (
    <RootContext.Provider
      value={{
        theme,
        minHeight: poll.minHeight,
        maxWidth: poll.maxWidth,
        template,
        trackingEngine,
        schemaEngine,
        prefersColorScheme,
      }}
    >
      <Component title={title} poll={pollWithInjectedVariables} />
    </RootContext.Provider>
  );
};

export interface PollComponentConfig {
  component: React.ComponentType<{ poll: PollType; title: string | null }>;
  trackingEngine: TrackingEngine;
  schemaEngine: SchemaEngine;
}
